/**
 * 表格元素组件
 * - 选中时显示行列控制浮层
 * - 行/列边线拖拽调整行高、列宽
 * - 点击单元格后 td[contenteditable] 原生进入编辑
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { TableElement as TableElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import {
  TABLE_MIN_ROW_HEIGHT,
  TABLE_MIN_COL_WIDTH,
} from '@/components/canvas/shared/constants/legacyConfig';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import {
  getRowHeights,
  getColWidths,
  getMergedRangeContaining,
} from '@/components/canvas/elements/table/utils/tableUtilsIndex';
import { tableLayoutSize } from '@/components/canvas/elements/table/utils/tableLayout';
import { appendBlurFilter, composeContainerTransform } from '../group/containerTransform';
import type { TableResizeDragState } from './types';
import { TableGrid } from './components/TableGrid';
import { TableOverlays } from './overlays/TableSelectionOverlay';
import { TableAxisControlsOverlay } from './overlays/TableAxisControlsOverlay';
import { blurRadius } from '@/components/canvas/elements/model';
import { createTextContentData } from '@/components/canvas/elements/text/factory';

export function TableElement({
  element,
  selected,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
  onDoubleClick,
}: ElementComponentProps<TableElementType>) {
  const { dx, dy } = dragOffset;
  const {
    updateElement,
    zoom,
    setSelectedTableColumn,
    setSelectedTableRow,
    setSelectedTableCells,
    selectedTableColumn,
    selectedTableRow,
    selectedTableCells,
    setEditingTableCellId,
  } = useCanvas();
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * 当前处于编辑状态的单元格 key（"row,col"）。
   * 通过双击进入，由 blur / Escape 退出，不依赖 contenteditable 的自动 focus。
   */
  const [editingCellKey, setEditingCellKey] = useState<string | null>(null);
  const editingCell = editingCellKey
    ? { row: parseInt(editingCellKey.split(',')[0]), col: parseInt(editingCellKey.split(',')[1]) }
    : null;

  const rows = Math.max(1, element.data.rows);
  const cols = Math.max(1, element.data.cols);
  const baseRowHeights = getRowHeights(element);
  const baseColWidths = getColWidths(element);
  const [resizePreview, setResizePreview] = useState<{
    rowHeights?: number[];
    colWidths?: number[];
  } | null>(null);
  const [resizeDrag, setResizeDrag] = useState<TableResizeDragState | null>(null);
  const rowHeights = resizePreview?.rowHeights ?? baseRowHeights;
  const colWidths = resizePreview?.colWidths ?? baseColWidths;
  const { width: layoutWidth, height: layoutHeight } = tableLayoutSize(colWidths, rowHeights);
  const pendingResizeRef = useRef<{ rowHeights?: number[]; colWidths?: number[] } | null>(null);
  pendingResizeRef.current = resizePreview;

  // 全局拖拽：把屏幕像素位移转为画布像素，mouseup 时用 ref 中的最新值提交
  useEffect(() => {
    if (!resizeDrag) return;
    const drag = resizeDrag;

    const handleMove = (e: MouseEvent) => {
      if (drag.type === 'row' && 'index' in drag) {
        const dyCanvas = (e.clientY - drag.startY) / zoom;
        const h = [...drag.startHeights];
        const r = drag.index;
        const next = Math.max(TABLE_MIN_ROW_HEIGHT, h[r] + dyCanvas);
        const delta = next - h[r];
        h[r] = next;
        h[r + 1] = Math.max(TABLE_MIN_ROW_HEIGHT, h[r + 1] - delta);
        setResizePreview({ rowHeights: h });
      } else if (drag.type === 'row-bottom') {
        const dyCanvas = (e.clientY - drag.startY) / zoom;
        const h = [...drag.startHeights];
        const last = h.length - 1;
        if (last >= 0) {
          h[last] = Math.max(TABLE_MIN_ROW_HEIGHT, h[last] + dyCanvas);
          setResizePreview({ rowHeights: h });
        }
      } else if (drag.type === 'col' && 'index' in drag) {
        const dxCanvas = (e.clientX - drag.startX) / zoom;
        const w = [...drag.startWidths];
        const c = drag.index;
        const next = Math.max(TABLE_MIN_COL_WIDTH, w[c] + dxCanvas);
        const delta = next - w[c];
        w[c] = next;
        w[c + 1] = Math.max(TABLE_MIN_COL_WIDTH, w[c + 1] - delta);
        setResizePreview({ colWidths: w });
      } else if (drag.type === 'col-right') {
        const dxCanvas = (e.clientX - drag.startX) / zoom;
        const w = [...drag.startWidths];
        const last = w.length - 1;
        if (last >= 0) {
          w[last] = Math.max(TABLE_MIN_COL_WIDTH, w[last] + dxCanvas);
          setResizePreview({ colWidths: w });
        }
      }
    };

    const handleUp = () => {
      const latest = pendingResizeRef.current;
      const isRowDrag = drag.type === 'row' || drag.type === 'row-bottom';
      const isColDrag = drag.type === 'col' || drag.type === 'col-right';
      if (isRowDrag && latest?.rowHeights) {
        const height = latest.rowHeights.reduce((a, b) => a + b, 0);
        updateElement(element.id, {
          data: { rowHeights: latest.rowHeights },
          size: { height },
        });
      } else if (isColDrag && latest?.colWidths) {
        const width = latest.colWidths.reduce((a, b) => a + b, 0);
        updateElement(element.id, {
          data: { colWidths: latest.colWidths },
          size: { width },
        });
      }
      setResizePreview(null);
      setResizeDrag(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [resizeDrag, zoom, element.id, updateElement]);

  const startRowResize = useCallback(
    (r: number, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const heights = rowHeights;
      setResizeDrag({ type: 'row', index: r, startY: e.clientY, startHeights: heights });
      setResizePreview({ rowHeights: heights });
    },
    [rowHeights]
  );

  const startColResize = useCallback(
    (c: number, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const widths = colWidths;
      setResizeDrag({ type: 'col', index: c, startX: e.clientX, startWidths: widths });
      setResizePreview({ colWidths: widths });
    },
    [colWidths]
  );

  const startRowEdgeResize = useCallback(
    (_edge: 'bottom', e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const heights = rowHeights;
      setResizeDrag({ type: 'row-bottom', startY: e.clientY, startHeights: heights });
      setResizePreview({ rowHeights: heights });
    },
    [rowHeights]
  );

  const startColEdgeResize = useCallback(
    (_edge: 'right', e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const widths = colWidths;
      setResizeDrag({ type: 'col-right', startX: e.clientX, startWidths: widths });
      setResizePreview({ colWidths: widths });
    },
    [colWidths]
  );

  /** 双击单元格 → 进入编辑模式 */
  const handleCellDoubleClick = useCallback(
    (r: number, c: number) => {
      const range = getMergedRangeContaining(element, r, c);
      const originR = range?.[0] ?? r;
      const originC = range?.[1] ?? c;
      setEditingCellKey(`${originR},${originC}`);
      // 同步更新单元格选中态
      const r1 = range?.[2] ?? originR;
      const c1 = range?.[3] ?? originC;
      setSelectedTableCells({ elementId: element.id, r0: originR, c0: originC, r1, c1 });
    },
    [element, setSelectedTableCells],
  );

  /** CellContent 实际 focus（进入 contenteditable）时通知 store 禁止拖拽 */
  const handleCellEditStart = useCallback(
    (_r: number, _c: number) => {
      setEditingTableCellId(element.id);
    },
    [element.id, setEditingTableCellId],
  );

  /** CellContent blur 时清理编辑态 */
  const handleCellEditEnd = useCallback(
    (_r: number, _c: number) => {
      setEditingCellKey(null);
      setEditingTableCellId(null);
    },
    [setEditingTableCellId],
  );

  /** 提交单元格文本 */
  const handleCellTextCommit = useCallback(
    (r: number, c: number, text: string) => {
      const key = `${r},${c}`;
      const existing = element.data.cells?.[key];
      const cleanText = text.replace(/\n$/, '');
      const nextText = cleanText
        ? createTextContentData({ content: cleanText })
        : undefined;
      updateElement(element.id, {
        data: {
          cells: {
            ...(element.data.cells ?? {}),
            [key]: { ...(existing ?? {}), text: nextText },
          },
        },
      });
    },
    [element.id, element.data.cells, updateElement],
  );

  /**
   * 单击单元格处理：
   * - 首次点击表格（tableJustSelectedRef=true）：仅保留表格选中，不进入单元格选中。
   * - 编辑中单击其他单元格：退出当前编辑，切换编辑到新格。
   * - 其他情况（表格已选中）：进入单元格选中态。
   */
  const handleCellClick = useCallback(
    (r: number, c: number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (tableJustSelectedRef.current) {
        tableJustSelectedRef.current = false;
        return;
      }
      if (editingCellKey) {
        // 编辑中点击另一格 → 切换编辑
        const range = getMergedRangeContaining(element, r, c);
        const originR = range?.[0] ?? r;
        const originC = range?.[1] ?? c;
        const newKey = `${originR},${originC}`;
        if (newKey !== editingCellKey) {
          setEditingCellKey(newKey);
          const r1 = range?.[2] ?? originR;
          const c1 = range?.[3] ?? originC;
          setSelectedTableCells({ elementId: element.id, r0: originR, c0: originC, r1, c1 });
        }
        return;
      }
      if (selected) {
        const range = getMergedRangeContaining(element, r, c);
        if (range) {
          const [r0, c0, r1, c1] = range;
          setSelectedTableCells({ elementId: element.id, r0, c0, r1, c1 });
        } else {
          setSelectedTableCells({ elementId: element.id, r0: r, c0: c, r1: r, c1: c });
        }
      }
    },
    [selected, editingCellKey, element, setSelectedTableCells],
  );

  const isCellRangeSelection =
    selected && selectedTableCells?.elementId === element.id;
  const cellRange = isCellRangeSelection
    ? {
        r0: Math.min(selectedTableCells!.r0, selectedTableCells!.r1),
        c0: Math.min(selectedTableCells!.c0, selectedTableCells!.c1),
        r1: Math.max(selectedTableCells!.r0, selectedTableCells!.r1),
        c1: Math.max(selectedTableCells!.c0, selectedTableCells!.c1),
      }
    : null;

  const [axisLineDragKind, setAxisLineDragKind] = useState<'row' | 'col' | null>(null);
  const [cellRangeDrag, setCellRangeDrag] = useState<'tl' | 'br' | null>(null);
  const cellRangeDragAnchorRef = useRef({ r0: 0, c0: 0, r1: 0, c1: 0 });
  /** 本次 mousedown 时表格尚未选中，则本次 click 只保留表格选中，不进入单元格选中 */
  const tableJustSelectedRef = useRef(false);
  useEffect(() => {
    if (!cellRangeDrag || !cellRange || !containerRef.current) return;
    if (cellRangeDrag === 'tl') {
      cellRangeDragAnchorRef.current = { r0: 0, c0: 0, r1: cellRange.r1, c1: cellRange.c1 };
    } else {
      cellRangeDragAnchorRef.current = { r0: cellRange.r0, c0: cellRange.c0, r1: 0, c1: 0 };
    }
    const el = containerRef.current;
    const handleMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const localX = (e.clientX - rect.left) * (layoutWidth / rect.width);
      const localY = (e.clientY - rect.top) * (layoutHeight / rect.height);
      let c = 0;
      let sum = 0;
      for (; c < cols; c++) {
        sum += colWidths[c];
        if (localX < sum) break;
      }
      const hitC = c >= cols ? cols - 1 : c;
      let r = 0;
      sum = 0;
      for (; r < rows; r++) {
        sum += rowHeights[r];
        if (localY < sum) break;
      }
      const hitR = r >= rows ? rows - 1 : r;
      const anchor = cellRangeDragAnchorRef.current;
      if (cellRangeDrag === 'tl') {
        setSelectedTableCells({
          elementId: element.id,
          r0: hitR,
          c0: hitC,
          r1: anchor.r1,
          c1: anchor.c1,
        });
      } else {
        setSelectedTableCells({
          elementId: element.id,
          r0: anchor.r0,
          c0: anchor.c0,
          r1: hitR,
          c1: hitC,
        });
      }
    };
    const handleUp = () => setCellRangeDrag(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [cellRangeDrag, cellRange, element.id, layoutWidth, layoutHeight, cols, rows, colWidths, rowHeights, setSelectedTableCells]);

  const style: React.CSSProperties = {
    position: 'absolute',
    left: element.layout.x + dx,
    top: element.layout.y + dy,
    width: layoutWidth,
    height: layoutHeight,
    opacity: element.appearance.opacity,
    transform: composeContainerTransform(element),
    filter: appendBlurFilter(undefined, blurRadius(element)),
  };

  const isCellEditing = editingCell !== null;
  const colHighlight =
    !isCellEditing &&
    selected &&
    selectedTableColumn?.elementId === element.id &&
    selectedTableColumn.col >= 0 &&
    selectedTableColumn.col < cols;
  const rowHighlight =
    !isCellEditing &&
    selected &&
    selectedTableRow?.elementId === element.id &&
    selectedTableRow.row >= 0 &&
    selectedTableRow.row < rows;

  const axisSelectedCol =
    selected && selectedTableColumn?.elementId === element.id
      ? selectedTableColumn.col
      : null;
  const axisSelectedRow =
    selected && selectedTableRow?.elementId === element.id ? selectedTableRow.row : null;
  const hasTableSubSelection =
    isCellRangeSelection ||
    axisSelectedRow != null ||
    axisSelectedCol != null;
  const showAxisControls = selected && hasTableSubSelection && !editingCell;
  const showAddStrips = selected && (isCellRangeSelection || editingCell != null);
  const showAxisOverlay = showAxisControls || showAddStrips;
  const axisAnchorRow =
    cellRange?.r0 ?? axisSelectedRow ?? editingCell?.row ?? 0;
  const axisAnchorCol =
    cellRange?.c0 ?? axisSelectedCol ?? editingCell?.col ?? 0;

  return (
    <div
      ref={containerRef}
      style={{ ...style, overflow: 'visible' }}
      className="pointer-events-auto cursor-inherit"
      data-element-hit
      data-element-id={element.id}
      onMouseDownCapture={() => {
        if (!selected) tableJustSelectedRef.current = true;
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (tableJustSelectedRef.current) {
          tableJustSelectedRef.current = false;
          setSelectedTableCells(null);
          setSelectedTableColumn(null);
          setSelectedTableRow(null);
        }
        onSelect(e);
      }}
      onMouseDown={(e) => onMouseDown?.(e, element)}
      onDoubleClick={(e) => onDoubleClick?.(e, element)}
    >
      <TableGrid
        element={element}
        rows={rows}
        cols={cols}
        rowHeights={rowHeights}
        colWidths={colWidths}
        editingCellKey={editingCellKey}
        onCellClick={handleCellClick}
        onCellDoubleClick={handleCellDoubleClick}
        onCellTextCommit={handleCellTextCommit}
        onCellEditStart={handleCellEditStart}
        onCellEditEnd={handleCellEditEnd}
      />

      {selected && showAxisOverlay && (
        <TableAxisControlsOverlay
          element={element}
          rows={rows}
          cols={cols}
          rowHeights={rowHeights}
          colWidths={colWidths}
          selectedCol={axisSelectedCol}
          selectedRow={axisSelectedRow}
          anchorRow={axisAnchorRow}
          anchorCol={axisAnchorCol}
          containerRef={containerRef}
          showAxisControls={showAxisControls}
          showAddStrips={showAddStrips}
          onRowEdgeResize={startRowEdgeResize}
          onColEdgeResize={startColEdgeResize}
          onRowResize={startRowResize}
          onColResize={startColResize}
          onLineDragChange={setAxisLineDragKind}
        />
      )}

      {selected && (
        <TableOverlays
          element={element}
          rows={rows}
          cols={cols}
          rowHeights={rowHeights}
          colWidths={colWidths}
          zoom={zoom}
          editingCell={editingCell}
          isCellRangeSelection={isCellRangeSelection}
          cellRange={cellRange}
          colHighlight={colHighlight && axisLineDragKind !== 'col'}
          rowHighlight={rowHighlight && axisLineDragKind !== 'row'}
          selectedTableColumn={selectedTableColumn}
          selectedTableRow={selectedTableRow}
          setCellRangeDrag={setCellRangeDrag}
        />
      )}
    </div>
  );
}
